import React, { useState, useMemo, useCallback } from 'react';
import { Settings, Search } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, getMetricObjectiveClassName, useComponentMetrics, panierMoyenPublicitaire } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

import { calculateVariation, renderVariation as renderVariationBase } from '../../utils/variationUtils';
/**
 * Composant table des termes de recherche Microsoft Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const SearchTermsTable = React.memo(({
  searchTermsData = [],
  pagination = null,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccount = null,
  itemsPerPage = 10,
  onPageChange = null,
  totals: propTotals = null,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_search_terms',
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = ''
}) => {
  // États locaux pour cette table
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showSearchTerms, setShowSearchTerms] = useState(true);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques sélectionnées (pour les couleurs COS/CPA)
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('msads_search_terms', selectedAccount?.id || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);


  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Map des termes de recherche précédents
  // Toujours créer le map si previousPeriodData existe (pas de dépendance à compareEnabled)
  const previousSearchTermsMap = useMemo(() => {
    if (!previousPeriodData?.searchTerms) return {};
    const map = {};
    previousPeriodData.searchTerms.forEach(term => {
      const key = term.searchTerm || term.query;
      if (key) map[key] = term;
    });
    return map;
  }, [previousPeriodData?.searchTerms]);

  // Fonction de tri memoized
  const sortedSearchTerms = useMemo(() => {
    if (!searchTermsData.length) return [];

    const sorted = [...searchTermsData];
    sorted.sort((a, b) => {
      // Le panier moyen n'est pas un champ des lignes : il se calcule, ici comme à l'affichage.
      let aValue = sortConfig.key === 'panier_moyen' ? panierMoyenPublicitaire(a) : a[sortConfig.key];
      let bValue = sortConfig.key === 'panier_moyen' ? panierMoyenPublicitaire(b) : b[sortConfig.key];
      
      // Cas spécial pour le terme de recherche
      if (sortConfig.key === 'searchTerm') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Pour les valeurs numériques
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
      
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [searchTermsData, sortConfig]);

  // Calcul des totaux memoized - utilise propTotals si disponible (totaux de TOUS les termes)
  const totals = useMemo(() => {
    // Si on a les totaux passés en prop (de tous les termes), les utiliser
    if (propTotals && (propTotals.impressions > 0 || propTotals.clicks > 0 || propTotals.cost > 0)) {
      return propTotals;
    }

    // Fallback: calculer sur les termes affichés uniquement
    if (!searchTermsData.length) return {};

    const baseTotals = searchTermsData.reduce((acc, term) => ({
      impressions: acc.impressions + (term.impressions || 0),
      clicks: acc.clicks + (term.clicks || 0),
      cost: acc.cost + (term.cost || 0),
      conversions: acc.conversions + (term.conversions || 0),
      conversionsValue: acc.conversionsValue + (term.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [searchTermsData, propTotals]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Ne pas rendre si pas de données
  if (!searchTermsData.length) return null;

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Search size={20} /> Termes de recherche
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Termes de recherche Microsoft Ads"
            />
          ) : (
            <button
              onClick={() => setShowSearchTerms(!showSearchTerms)}
              className="section-toggle-btn"
              title={showSearchTerms ? "Masquer la section" : "Afficher la section"}
            >
              {showSearchTerms ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && showSearchTerms && (
        <>
          <div className="campaigns-table-wrapper">
            <table className="campaigns-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('searchTerm')} className="sortable">
                    Terme de recherche {sortConfig.key === 'searchTerm' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricActive('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('panier_moyen') && (
                    <th onClick={() => handleSort('panier_moyen')} className="sortable numeric">
                      Panier moyen {sortConfig.key === 'panier_moyen' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roas') && (
                    <th onClick={() => handleSort('roas')} className="sortable numeric">
                      ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roi') && (
                    <th onClick={() => handleSort('roi')} className="sortable numeric">
                      ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {sortedSearchTerms.map((term, index) => {
                  const termKey = term.searchTerm || term.query;
                  const prevTerm = previousSearchTermsMap[termKey] || {};
                  const prevCtr = prevTerm.impressions > 0 ? (prevTerm.clicks / prevTerm.impressions * 100) : 0;
                  const prevCpc = prevTerm.clicks > 0 ? (prevTerm.cost / prevTerm.clicks) : 0;
                  const prevConvRate = prevTerm.clicks > 0 ? (prevTerm.conversions / prevTerm.clicks * 100) : 0;
                  const prevCpa = prevTerm.conversions > 0 ? (prevTerm.cost / prevTerm.conversions) : 0;
                  const prevCos = prevTerm.conversionsValue > 0 ? (prevTerm.cost / prevTerm.conversionsValue * 100) : 0;

                  return (
                    <tr key={term.id || index}>
                      <td className="search-term">
                        {term.searchTerm || term.query || 'N/A'}
                      </td>
                      {isComponentMetricActive('impressions') && (
                        <td className="numeric">
                          {formatNumber(term.impressions || 0)}
                          {renderVariation(calculateVariation(term.impressions || 0, prevTerm.impressions || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('ctr') && (
                        <td className="numeric">
                          {formatNumber(term.ctr || 0, 2)}%
                          {renderVariation(calculateVariation(term.ctr || 0, prevCtr))}
                        </td>
                      )}
                      {isComponentMetricActive('clics') && (
                        <td className="numeric">
                          {formatNumber(term.clicks || 0)}
                          {renderVariation(calculateVariation(term.clicks || 0, prevTerm.clicks || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cpc') && (
                        <td className="numeric">
                          €{formatNumber(term.cpc || 0, 2)}
                          {renderVariation(calculateVariation(term.cpc || 0, prevCpc), true)}
                        </td>
                      )}
                      {isComponentMetricActive('cout') && (
                        <td className="numeric">
                          €{formatNumber(term.cost || 0, 2)}
                          {renderVariation(calculateVariation(term.cost || 0, prevTerm.cost || 0), true)}
                        </td>
                      )}
                      {isComponentMetricActive('taux_conv') && (
                        <td className="numeric">
                          {formatNumber(term.conversionRate || 0, 2)}%
                          {renderVariation(calculateVariation(term.conversionRate || 0, prevConvRate))}
                        </td>
                      )}
                      {isComponentMetricActive('conv') && (
                        <td className="numeric">
                          {formatNumber(term.conversions || 0, 1)}
                          {renderVariation(calculateVariation(term.conversions || 0, prevTerm.conversions || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cpa') && (
                        <td className={`numeric ${getCpaClassName(term.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                          €{formatNumber(term.cpa || 0, 2)}
                          {renderVariation(calculateVariation(term.cpa || 0, prevCpa), true)}
                        </td>
                      )}
                      {isComponentMetricActive('panier_moyen') && (
                        <td className="numeric">
                          €{formatNumber(panierMoyenPublicitaire(term), 2)}
                          {renderVariation(calculateVariation(panierMoyenPublicitaire(term), panierMoyenPublicitaire(prevTerm)))}
                        </td>
                      )}
                      {isComponentMetricActive('valeur_conv') && (
                        <td className="numeric">
                          €{formatNumber(term.conversionsValue || 0, 2)}
                          {renderVariation(calculateVariation(term.conversionsValue || 0, prevTerm.conversionsValue || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cos') && (
                        <td className={`numeric ${getCosClassName(term.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                          {formatNumber(term.cos || 0, 1)}%
                          {renderVariation(calculateVariation(term.cos || 0, prevCos), true)}
                        </td>
                      )}
                      {isComponentMetricActive('roas') && (() => {
                        const roas = term.cost > 0 ? (term.conversionsValue / term.cost) * 100 : 0;
                        const prevRoas = prevTerm.cost > 0 ? (prevTerm.conversionsValue / prevTerm.cost) * 100 : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                            {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
                            {renderVariation(calculateVariation(roas, prevRoas))}
                          </td>
                        );
                      })()}
                      {isComponentMetricActive('roi') && (() => {
                        const hasData = term.cost > 0 && term.conversionsValue > 0;
                        const roi = hasData ? (term.conversionsValue - term.cost) / term.cost : 0;
                        const prevHasData = prevTerm.cost > 0 && prevTerm.conversionsValue > 0;
                        const prevRoi = prevHasData ? (prevTerm.conversionsValue - prevTerm.cost) / prevTerm.cost : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                            {formatNumber(roi, 2)}
                            {renderVariation(calculateVariation(roi, prevRoi))}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                {(() => {
                  // Calculer les totaux de la période précédente
                  const prevSearchTerms = previousPeriodData?.searchTerms || [];
                  const prevTotals = prevSearchTerms.reduce((acc, term) => ({
                    impressions: acc.impressions + (term.impressions || 0),
                    clicks: acc.clicks + (term.clicks || 0),
                    cost: acc.cost + (term.cost || 0),
                    conversions: acc.conversions + (term.conversions || 0),
                    conversionsValue: acc.conversionsValue + (term.conversionsValue || 0)
                  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

                  const prevCtr = prevTotals.impressions > 0 ? (prevTotals.clicks / prevTotals.impressions * 100) : 0;
                  const prevCpc = prevTotals.clicks > 0 ? (prevTotals.cost / prevTotals.clicks) : 0;
                  const prevConvRate = prevTotals.clicks > 0 ? (prevTotals.conversions / prevTotals.clicks * 100) : 0;
                  const prevCpa = prevTotals.conversions > 0 ? (prevTotals.cost / prevTotals.conversions) : 0;
                  const prevCos = prevTotals.conversionsValue > 0 ? (prevTotals.cost / prevTotals.conversionsValue * 100) : 0;

                  return (
                    <tr className="total-row">
                      <td className="search-term"><strong>TOTAL</strong></td>
                      {isComponentMetricActive('impressions') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.impressions)}</strong>
                          {renderVariation(calculateVariation(totals.impressions, prevTotals.impressions))}
                        </td>
                      )}
                      {isComponentMetricActive('ctr') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.ctr, 2)}%</strong>
                          {renderVariation(calculateVariation(totals.ctr, prevCtr))}
                        </td>
                      )}
                      {isComponentMetricActive('clics') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.clicks)}</strong>
                          {renderVariation(calculateVariation(totals.clicks, prevTotals.clicks))}
                        </td>
                      )}
                      {isComponentMetricActive('cpc') && (
                        <td className="numeric">
                          <strong>€{formatNumber(totals.cpc, 2)}</strong>
                          {renderVariation(calculateVariation(totals.cpc, prevCpc), true)}
                        </td>
                      )}
                      {isComponentMetricActive('cout') && (
                        <td className="numeric">
                          <strong>€{formatNumber(totals.cost, 2)}</strong>
                          {renderVariation(calculateVariation(totals.cost, prevTotals.cost), true)}
                        </td>
                      )}
                      {isComponentMetricActive('taux_conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
                          {renderVariation(calculateVariation(totals.conversionRate, prevConvRate))}
                        </td>
                      )}
                      {isComponentMetricActive('conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.conversions, 1)}</strong>
                          {renderVariation(calculateVariation(totals.conversions, prevTotals.conversions))}
                        </td>
                      )}
                      {isComponentMetricActive('cpa') && (
                        <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>€{formatNumber(totals.cpa, 2)}</strong>
                          {renderVariation(calculateVariation(totals.cpa, prevCpa), true)}
                        </td>
                      )}
                      {isComponentMetricActive('panier_moyen') && (
                        <td className="numeric">
                          <strong>€{formatNumber(panierMoyenPublicitaire(totals), 2)}</strong>
                          {renderVariation(calculateVariation(panierMoyenPublicitaire(totals), panierMoyenPublicitaire(prevTotals)))}
                        </td>
                      )}
                      {isComponentMetricActive('valeur_conv') && (
                        <td className="numeric">
                          <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
                          {renderVariation(calculateVariation(totals.conversionsValue, prevTotals.conversionsValue))}
                        </td>
                      )}
                      {isComponentMetricActive('cos') && (
                        <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>{formatNumber(totals.cos, 1)}%</strong>
                          {renderVariation(calculateVariation(totals.cos, prevCos), true)}
                        </td>
                      )}
                      {isComponentMetricActive('roas') && (() => {
                        const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
                        const prevRoas = prevTotals.cost > 0 ? (prevTotals.conversionsValue / prevTotals.cost) * 100 : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                            <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
                            {renderVariation(calculateVariation(roas, prevRoas))}
                          </td>
                        );
                      })()}
                      {isComponentMetricActive('roi') && (() => {
                        const hasData = totals.cost > 0 && totals.conversionsValue > 0;
                        const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
                        const prevHasData = prevTotals.cost > 0 && prevTotals.conversionsValue > 0;
                        const prevRoi = prevHasData ? (prevTotals.conversionsValue - prevTotals.cost) / prevTotals.cost : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                            <strong>{formatNumber(roi, 2)}</strong>
                            {renderVariation(calculateVariation(roi, prevRoi))}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {pagination && pagination.totalPages && pagination.totalPages > 1 && onPageChange && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Page {pagination.current || 1} sur {pagination.totalPages} ({pagination.total || searchTermsData.length} termes de recherche au total)
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => onPageChange(Math.max(1, (pagination.current || 1) - 1))}
                  disabled={pagination.current === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <button
                  onClick={() => onPageChange(Math.min(pagination.totalPages, (pagination.current || 1) + 1))}
                  disabled={pagination.current === pagination.totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="msads_search_terms"
          accountId={selectedAccount?.id || 'global'}
          title="Termes de recherche Microsoft Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

SearchTermsTable.displayName = 'SearchTermsTable';

export default SearchTermsTable;